import {FC, PropsWithChildren, useMemo} from "react";
import {useParams} from "react-router";
import {useMarketCategories, useMarketItems} from "@hooks/selectors/services.selectors";
import {injectChild} from "@components/constructor";
import {MarketCategoryProvider} from "@constructor/components/features/market/categories/context";
import {useGlobalVariable} from "@hooks/selectors/panel.selectors";
import {useGameServerAuto, useLoginServerAuto} from "@hooks/selectors/game.selectors";
import {useItems} from "@hooks/selectors/user/item.selectors";
import {checkItemServers} from "../../../../../../helpers/utils";

interface MarketCategoryProps {
    category_id?: string;
    default_category?: string;
    filter_items_by_login?: boolean;
    filter_items_by_game?: boolean;
}

interface MarketCategoryChildProps {
    market_category_loading: boolean;
    market_category_loaded: boolean;
    market_category_error: boolean;
    market_category_items_count: number;
}

const MarketCategory: FC<PropsWithChildren<MarketCategoryProps>> = ({ category_id, default_category, filter_items_by_login, filter_items_by_game, children }) => {

    const {category_url} = useParams<{category_url?: string}>();
    const globalCategoryId = useGlobalVariable('market_category_id', category_id);

    const login = useLoginServerAuto();
    const game = useGameServerAuto();
    const { loading, loaded, error, categories = []} = useMarketCategories();

    const category = categories.find(x =>
            x.id === category_id
            || x.id === globalCategoryId
            || x.shortName === category_url)
        || categories.find(x => x.shortName === default_category);

    const { items } = useMarketItems(category?.id);
    const categoryItems = useItems(items?.map(x => x.itemId) || []);
    const itemsLoading = categoryItems.some(item => item.loading);
    const itemsCount = categoryItems.filter(item => {
        if (!item) return false;

        if (filter_items_by_login && login && !checkItemServers(item, login.id)) return false;
        if (filter_items_by_game && game && !checkItemServers(item, game.id)) return false;

        return true;
    }).length || 0;

    const data = useMemo<MarketCategoryChildProps>(() => ({
        market_category_error: Boolean(error),
        market_category_loaded: loaded,
        market_category_loading: loading || itemsLoading,
        market_category_items_count: itemsCount
    }), [itemsCount, error, loaded, loading, itemsLoading]);

    if(category) {
        return (
            <MarketCategoryProvider value={category}>
                {injectChild(data, children)}
            </MarketCategoryProvider>
        )
    }

    return injectChild(data, children);
}

export default MarketCategory;